import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListModelRoutingResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Setup > Which model answers (F-114).
 *
 * Two things, in the order somebody does them: the accounts this workspace
 * pays for, then the order models are tried in for each kind of request.
 *
 * The key field is write-only. It reports whether a key is stored and shows a
 * blank box otherwise, and saving a blank keeps what is there - so editing a
 * label cannot wipe a credential, and no screen in the product can display
 * one.
 */

type Provider = ListModelRoutingResult['providers'][number];
type Task = ListModelRoutingResult['tasks'][number];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';
const KINDS: Provider['kind'][] = ['cerebras', 'openai', 'anthropic', 'custom'];

export function ModelRoutingPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const entitled = useEntitlement('F-105');
  const { can } = useSession();
  const allowed = entitled.enabled && can('setup.roles.manage');
  const data = useQuery<ListModelRoutingResult, ApiError>({
    queryKey: ['model-routing'],
    queryFn: () => api.listModelRouting({}),
    enabled: allowed,
  });

  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<Provider['kind']>('openai');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['model-routing'] });

  const addProvider = useMutation({
    mutationFn: () =>
      api.saveModelProvider({
        body: { providerId: null, label, kind, baseUrl: baseUrl.trim() || null, apiKey: apiKey.trim() || null, enabled: true },
      }),
    onSuccess: async () => {
      setLabel('');
      setBaseUrl('');
      setApiKey('');
      await refresh();
    },
  });
  const removeProvider = useMutation({
    mutationFn: (providerId: string) => api.deleteModelProvider({ path: { providerId }, body: {} }),
    onSuccess: refresh,
  });
  const saveChain = useMutation({
    mutationFn: (input: { task: Task; steps: Array<{ providerId: string | null; model: string | null; enabled: boolean }> }) =>
      api.setModelRoutes({ path: { task: input.task }, body: { steps: input.steps } }),
    onSuccess: refresh,
  });

  if (!allowed) return null;
  if (data.isPending) return <Skeleton className="h-plot w-full" />;
  if (data.isError) return null;

  const chainFor = (task: Task) =>
    data.data.routes
      .filter((step) => step.task === task)
      .map((step) => ({ providerId: step.providerId, model: step.model, enabled: step.enabled }));

  return (
    <Panel data-region="model-routing" id="model-routing">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('routing.model.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('routing.model.body')}</p>
        </div>

        <ul data-model-providers className="flex flex-col gap-1">
          {data.data.providers.map((provider) => (
            <li
              key={provider.providerId}
              data-model-provider={provider.label}
              className="flex flex-wrap items-baseline gap-2 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
            >
              <span className="text-dense text-ink">{provider.label}</span>
              <span className="text-meta text-ink-meta">{provider.kind}</span>
              {/* Whether a key is held, never the key. */}
              <span
                data-model-key={provider.hasKey ? 'set' : 'missing'}
                className={
                  provider.hasKey
                    ? 'rounded-chip bg-success-tint px-1.5 text-meta text-success-text'
                    : 'rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text'
                }
              >
                {t(provider.hasKey ? 'routing.model.keySet' : 'routing.model.keyMissing')}
              </span>
              <Button intent="ghost" size="small" className="ms-auto" onClick={() => removeProvider.mutate(provider.providerId)}>
                {t('routing.model.remove')}
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-end gap-2">
          <input data-model-label aria-label={t('routing.model.label')} placeholder={t('routing.model.label')} className={INPUT} value={label} onChange={(event) => setLabel(event.target.value)} />
          <select data-model-kind aria-label={t('routing.model.kind')} className={INPUT} value={kind} onChange={(event) => setKind(event.target.value as Provider['kind'])}>
            {KINDS.map((entry) => (
              <option key={entry} value={entry}>{entry}</option>
            ))}
          </select>
          <input data-model-base-url aria-label={t('routing.model.baseUrl')} placeholder={t('routing.model.baseUrl')} className={INPUT} value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} />
          {/* Write-only: a screen that can show a key is a screen that can
              leak one, and it is never needed again after it is pasted. */}
          <input data-model-key-input type="password" aria-label={t('routing.model.key')} placeholder={t('routing.model.key')} className={INPUT} value={apiKey} onChange={(event) => setApiKey(event.target.value)} />
          <Button intent="secondary" size="small" data-model-add disabled={addProvider.isPending || label.trim() === ''} onClick={() => addProvider.mutate()}>
            {t('routing.model.add')}
          </Button>
        </div>
        {addProvider.error ? <p data-model-refusal className="text-dense text-danger-text">{errorText(addProvider.error)}</p> : null}

        {/* The chain per task. Order is the content: first one that answers
            wins, and the deployment's own account is always last. */}
        {data.data.tasks.map((task) => {
          const steps = chainFor(task);
          return (
            <div key={task} data-model-chain={task} className="flex flex-col gap-1">
              <SectionLabel>{t(`routing.model.task.${task}` as 'routing.model.task.agent')}</SectionLabel>
              <ol className="flex flex-col gap-1">
                {steps.length === 0 ? (
                  <li className="text-meta text-ink-meta">{t('routing.model.default')}</li>
                ) : null}
                {steps.map((step, index) => (
                  <li key={index} data-chain-step={index + 1} className="flex flex-wrap items-center gap-2">
                    <span className="text-dense text-ink">
                      {String(index + 1)}. {data.data.providers.find((p) => p.providerId === step.providerId)?.label ?? t('routing.model.ours')}
                    </span>
                    <span className="text-meta text-ink-meta">{step.model ?? t('routing.model.defaultModel')}</span>
                    <Button
                      intent="ghost"
                      size="small"
                      className="ms-auto"
                      onClick={() => saveChain.mutate({ task, steps: steps.filter((_, at) => at !== index) })}
                    >
                      {t('routing.model.remove')}
                    </Button>
                  </li>
                ))}
              </ol>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-chain-add={task}
                  aria-label={t('routing.model.addStep')}
                  className={INPUT}
                  value=""
                  onChange={(event) => {
                    if (!event.target.value) return;
                    const providerId = event.target.value === 'ours' ? null : event.target.value;
                    saveChain.mutate({ task, steps: [...steps, { providerId, model: null, enabled: true }] });
                  }}
                >
                  <option value="">{t('routing.model.addStep')}</option>
                  <option value="ours">{t('routing.model.ours')}</option>
                  {data.data.providers.map((provider) => (
                    <option key={provider.providerId} value={provider.providerId}>{provider.label}</option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
        {saveChain.error ? <p data-chain-refusal className="text-dense text-danger-text">{errorText(saveChain.error)}</p> : null}
      </div>
    </Panel>
  );
}
